import { useQuery } from '@apollo/client';
import { getDataFromTree } from '@apollo/client/react/ssr';
import { NextPage } from 'next';
import { ApolloPageContext, PageProps } from 'helpers/next-page';
// Api
import { GET_MEMORABILIA } from 'api/memorabilia/queries';
// Types
import {
  GetMemorabilia,
  GetMemorabiliaVariables,
} from 'api/memorabilia/types/GetMemorabilia';
import {
  MemorabiliaOrderBy,
  MemorabiliaStatus,
  SortDirection,
} from 'api/graphql-global-types';
// Helpers
import { getMemorabiliaTabSeo } from 'helpers/athleteTabSeo';
import { getAthleteTabPageProps } from 'helpers/athleteTabPageGuard';
import { getEnvLink } from 'helpers/routes';
import { generateItemListStructuredData } from 'helpers/structured-data';
// Hocks
import withApollo from 'hocs/withApollo';
// Hooks
import {
  useRedirectOnError,
  useRedirectIfNoContent,
  useAthletePageData,
} from 'hooks';
// Layouts
import { getProfileAvatarPreload } from 'helpers/profileAvatar';
import Head from 'layouts/Head/Head';
import Header from 'layouts/Header/Header';
import Main from 'layouts/Main/Main';
import Footer from 'layouts/Footer/Footer';
// Context
import { FiltersProvider } from 'components/common3/PageWithFiltersWrapper/PageWithFiltersContext';
// Components
import AppBannerAndModal from 'components/common3/AppBannerAndModal/AppBannerAndModal';
import Description from 'components/Athlete/Description/Description';
import AllMemorabiliaContainer from 'components/AllMemorabilia/AllMemorabilia';
import NotFoundPage from 'components/NotFoundPage/NotFoundPage';

const MemorabiliaContent = (): JSX.Element | null => {
  const {
    error,
    skip,
    cleanedSlug,
    athleteSlug,
    metaData,
    store,
  } = useAthletePageData();

  const { data: memorabiliaData } = useQuery<
    GetMemorabilia,
    GetMemorabiliaVariables
  >(GET_MEMORABILIA, {
    variables: {
      input: {
        storeIds: [store?.id || ''],
        statuses: [MemorabiliaStatus.Active],
      },
    },
    skip: !store,
  });

  const memorabilia = memorabiliaData?.getMemorabilia?.entities;

  useRedirectOnError({ error, redirectTo: `/${athleteSlug}` });

  // Redirect to profile home if neither memorabilia nor product content is available)
  useRedirectIfNoContent({
    hasContent: (store?.hasMemorabilia || store?.hasPureProducts) ?? false,
    redirectTo: `/${athleteSlug}`,
    skip: !store || skip,
  });

  if (!store) {
    return null;
  }

  const canonicalLink = `${getEnvLink()}/${cleanedSlug}/memorabilia`;

  const memorabiliaItems =
    memorabilia?.map((item) => ({
      url: `${getEnvLink()}/${item.store.slug}/memorabilia/${item.slug}`,
    })) || [];

  const tabSeo = getMemorabiliaTabSeo(metaData.name, {
    hasMemorabilia: store.hasMemorabilia,
    hasPureProducts: store.hasPureProducts,
  });

  const structuredData = generateItemListStructuredData(
    `${metaData.name} ${store.hasMemorabilia ? 'Memorabilia' : 'Products'}`,
    memorabiliaItems
  );

  return (
    <>
      <Head
        title={tabSeo.title}
        description={tabSeo.description}
        image={metaData.avatarUrl}
        imagePreload={getProfileAvatarPreload(metaData.avatarUrl)}
        canonicalLink={canonicalLink}
        robots={
          store.hasMemorabilia || store.hasPureProducts
            ? 'index, follow'
            : 'noindex, follow'
        }
        structuredData={structuredData}
      />
      <AppBannerAndModal
        when
        variant="profile"
        username={cleanedSlug ?? undefined}
        profileImageUrl={metaData.avatarUrl ?? undefined}
      />
      <Header />

      <Main backgroundColor="background-dim" withoutSidesPadding>
        <Description store={store} />
        {(store.hasMemorabilia || store.hasPureProducts) && (
          <FiltersProvider
            storesListFilter="hasActiveProducts"
            searchKey="searchTerm"
            hideContentCreatorsFilter
            hideAthletesFilter
            hideOrganizationsFilter
            hideSports
            hideProductType
            hasAthleteSlug
            initialSorting={{
              direction: SortDirection.DESC,
              order: MemorabiliaOrderBy.createdAt,
            }}
          >
            <AllMemorabiliaContainer
              isPage={false}
              title="Memorabilia"
              storeSlug={cleanedSlug as string | undefined}
              singleCategory
            />
          </FiltersProvider>
        )}
      </Main>

      <Footer />
    </>
  );
};

const Memorabilia: NextPage<PageProps> = ({ isNotFound }) =>
  isNotFound ? <NotFoundPage /> : <MemorabiliaContent />;

Memorabilia.getInitialProps = async (
  ctx: ApolloPageContext
): Promise<PageProps> => getAthleteTabPageProps(ctx, 'memorabilia');

export default withApollo(Memorabilia, { getDataFromTree });
